import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api, { endpoints } from '../../utils/apiClient';
import { format } from 'date-fns';

const AdminTournaments = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTournament, setEditingTournament] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    game: '',
    prize: '',
    date: '',
    maxParticipants: 32,
    thumbnail: '',
    description: '',
    rules: [''],
    schedule: [{ phase: 'Registration', date: '', status: 'Upcoming' }],
    prizes: [{ place: '1st', amount: '', extras: '' }]
  });

  // Fetch all tournaments on component mount
  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const response = await api.get(endpoints.admin.tournaments.list);
      setTournaments(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching tournaments:', error);
      toast.error('Failed to fetch tournaments');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleArrayInputChange = (e, index, arrayName, fieldName = null) => {
    const { value } = e.target;
    setFormData(prevData => {
      const newArray = [...prevData[arrayName]];
      
      if (fieldName) {
        // For nested objects in arrays (like schedule or prizes)
        newArray[index] = { 
          ...newArray[index], 
          [fieldName]: value 
        };
      } else {
        // For simple arrays (like rules)
        newArray[index] = value;
      }
      
      return {
        ...prevData,
        [arrayName]: newArray
      };
    });
  };

  const addArrayItem = (arrayName, defaultItem) => {
    setFormData(prevData => ({
      ...prevData,
      [arrayName]: [...prevData[arrayName], defaultItem]
    }));
  };

  const removeArrayItem = (arrayName, index) => {
    setFormData(prevData => ({
      ...prevData,
      [arrayName]: prevData[arrayName].filter((_, i) => i !== index)
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      game: '',
      prize: '',
      date: '',
      maxParticipants: 32,
      thumbnail: '',
      description: '',
      rules: [''],
      schedule: [{ phase: 'Registration', date: '', status: 'Upcoming' }],
      prizes: [{ place: '1st', amount: '', extras: '' }]
    });
    setEditingTournament(null);
  };

  const handleEditClick = (tournament) => {
    setEditingTournament(tournament);
    
    // Format date for the form
    const formattedDate = tournament.date ? 
      format(new Date(tournament.date), "yyyy-MM-dd'T'HH:mm") : 
      '';
    
    // Format schedule dates
    const formattedSchedule = tournament.schedule?.map(item => ({
      ...item,
      date: item.date ? format(new Date(item.date), "yyyy-MM-dd'T'HH:mm") : ''
    })) || [{ phase: 'Registration', date: '', status: 'Upcoming' }];
    
    setFormData({
      title: tournament.title || '',
      game: tournament.game || '',
      prize: tournament.prize || '',
      date: formattedDate,
      maxParticipants: tournament.maxParticipants || 32,
      thumbnail: tournament.thumbnail || '',
      description: tournament.description || '',
      rules: tournament.rules?.length ? tournament.rules : [''],
      schedule: formattedSchedule,
      prizes: tournament.prizes?.length ? 
        tournament.prizes : 
        [{ place: '1st', amount: '', extras: '' }],
      status: tournament.status || 'Registration Open'
    });
    
    setShowCreateModal(true);
  };

  const handleDeleteClick = async (tournamentId) => {
    if (!window.confirm('Are you sure you want to delete this tournament?')) {
      return;
    }
    
    try {
      await api.delete(endpoints.admin.tournaments.delete(tournamentId));
      toast.success('Tournament deleted successfully');
      fetchTournaments();
    } catch (error) {
      console.error('Error deleting tournament:', error);
      toast.error('Failed to delete tournament');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Remove empty rules
      const cleanRules = formData.rules.filter(rule => rule.trim() !== '');
      
      // Clean up schedule and prizes
      const cleanSchedule = formData.schedule.filter(phase => 
        phase.phase.trim() !== '' && phase.date !== ''
      );
      
      const cleanPrizes = formData.prizes.filter(prize => 
        prize.place.trim() !== '' || prize.amount.trim() !== '' || prize.extras.trim() !== ''
      );
      
      // Prepare the data
      const submitData = {
        ...formData,
        maxParticipants: Number(formData.maxParticipants),
        rules: cleanRules,
        schedule: cleanSchedule,
        prizes: cleanPrizes
      };
      
      if (editingTournament) {
        // Update existing tournament
        await api.put(
          endpoints.admin.tournaments.update(editingTournament._id), 
          submitData
        );
        toast.success('Tournament updated successfully');
      } else {
        // Create new tournament
        await api.post(endpoints.admin.tournaments.create, submitData);
        toast.success('Tournament created successfully');
      }
      
      resetForm();
      setShowCreateModal(false);
      fetchTournaments();
    } catch (error) {
      console.error('Error saving tournament:', error);
      toast.error(error.message || 'Failed to save tournament');
    } finally {
      setLoading(false);
    }
  };

  // Filter tournaments based on search term
  const filteredTournaments = tournaments.filter(tournament => 
    tournament.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tournament.game?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tournament.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    return format(new Date(dateString), 'MMM dd, yyyy');
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">Tournament Management</h2>
        <div className="flex space-x-2">
          <div className="relative mr-4">
            <input
              type="text"
              placeholder="Search tournaments..."
              className="px-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <svg
              className="w-5 h-5 absolute right-3 top-3 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              ></path>
            </svg>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition duration-200"
          >
            Create Tournament
          </button>
        </div>
      </div>

      {loading && tournaments.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-gray-800 rounded-lg overflow-hidden">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Tournament
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Game
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Prize
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Participants
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredTournaments.length > 0 ? (
                filteredTournaments.map((tournament) => (
                  <tr key={tournament._id} className="hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={tournament.thumbnail || 'https://placehold.co/200x200?text=Tournament'}
                            alt={tournament.title}
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">{tournament.title}</div>
                          <div className="text-sm text-gray-400 max-w-xs truncate">{tournament.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">{tournament.game}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">{tournament.prize}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">{formatDate(tournament.date)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        tournament.status === 'Registration Open' ? 'bg-green-100 text-green-800' :
                        tournament.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                        tournament.status === 'Completed' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {tournament.status || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">
                        {tournament.participants?.length || 0} / {tournament.maxParticipants || 0}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEditClick(tournament)}
                        className="text-indigo-400 hover:text-indigo-300 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(tournament._id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-400">
                    {searchTerm ? 'No tournaments found matching your search.' : 'No tournaments found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Tournament Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 overflow-y-auto">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-4xl my-8">
            <h3 className="text-xl font-semibold mb-4">
              {editingTournament ? 'Edit Tournament' : 'Create New Tournament'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Game
                  </label>
                  <input
                    type="text"
                    name="game"
                    value={formData.game}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Prize
                  </label>
                  <input
                    type="text"
                    name="prize"
                    value={formData.prize}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. 1000 BITP"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Date
                  </label>
                  <input
                    type="datetime-local"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Max Participants
                  </label>
                  <input
                    type="number"
                    name="maxParticipants"
                    value={formData.maxParticipants}
                    onChange={handleInputChange}
                    min="2"
                    required
                    className="w-full px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Thumbnail URL
                  </label>
                  <input
                    type="text"
                    name="thumbnail"
                    value={formData.thumbnail}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    required
                    className="w-full px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  ></textarea>
                </div>
                
                {editingTournament && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Status
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Registration Open">Registration Open</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>
                )}
              </div>
              
              {/* Rules Section */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Tournament Rules
                  </label>
                  <button
                    type="button"
                    onClick={() => addArrayItem('rules', '')}
                    className="text-blue-400 hover:text-blue-300 text-sm"
                  >
                    + Add Rule
                  </button>
                </div>
                
                {formData.rules.map((rule, index) => (
                  <div key={`rule-${index}`} className="flex mb-2">
                    <input
                      type="text"
                      value={rule}
                      onChange={(e) => handleArrayInputChange(e, index, 'rules')}
                      className="flex-grow px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mr-2"
                      placeholder="Enter tournament rule"
                    />
                    <button
                      type="button"
                      onClick={() => removeArrayItem('rules', index)}
                      className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Schedule Section */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Tournament Schedule
                  </label>
                  <button
                    type="button"
                    onClick={() => addArrayItem('schedule', { phase: '', date: '', status: 'Upcoming' })}
                    className="text-blue-400 hover:text-blue-300 text-sm"
                  >
                    + Add Phase
                  </button>
                </div>
                
                {formData.schedule.map((phase, index) => (
                  <div key={`schedule-${index}`} className="grid grid-cols-3 gap-2 mb-2">
                    <input
                      type="text"
                      value={phase.phase}
                      onChange={(e) => handleArrayInputChange(e, index, 'schedule', 'phase')}
                      className="px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Phase name"
                    />
                    <input
                      type="datetime-local"
                      value={phase.date}
                      onChange={(e) => handleArrayInputChange(e, index, 'schedule', 'date')}
                      className="px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="flex">
                      <select
                        value={phase.status}
                        onChange={(e) => handleArrayInputChange(e, index, 'schedule', 'status')}
                        className="flex-grow px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mr-2"
                      >
                        <option value="Upcoming">Upcoming</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => removeArrayItem('schedule', index)}
                        className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        &times;
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Prizes Section */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Prize Distribution
                  </label>
                  <button
                    type="button"
                    onClick={() => addArrayItem('prizes', { place: '', amount: '', extras: '' })}
                    className="text-blue-400 hover:text-blue-300 text-sm"
                  >
                    + Add Prize
                  </button>
                </div>
                
                {formData.prizes.map((prize, index) => (
                  <div key={`prize-${index}`} className="grid grid-cols-3 gap-2 mb-2">
                    <input
                      type="text"
                      value={prize.place}
                      onChange={(e) => handleArrayInputChange(e, index, 'prizes', 'place')}
                      className="px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Place (e.g. 1st)"
                    />
                    <input
                      type="text"
                      value={prize.amount}
                      onChange={(e) => handleArrayInputChange(e, index, 'prizes', 'amount')}
                      className="px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Amount (e.g. 500 BITP)"
                    />
                    <div className="flex">
                      <input
                        type="text"
                        value={prize.extras}
                        onChange={(e) => handleArrayInputChange(e, index, 'prizes', 'extras')}
                        className="flex-grow px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mr-2"
                        placeholder="Extras"
                      />
                      <button
                        type="button"
                        onClick={() => removeArrayItem('prizes', index)}
                        className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        &times;
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-end mt-6 space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition duration-200"
                >
                  {loading ? 'Saving...' : editingTournament ? 'Update Tournament' : 'Create Tournament'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTournaments;
